import { useState, useCallback, useRef, useEffect } from "react";
import { useMutation, useQuery } from "@apollo/client/react";
import { type ResultOf, type VariablesOf } from "gql.tada";
import {
  CREATE_DRAFT_CALCULATION,
  RETRY_DRAFT_CALCULATION,
  CALCULATION_JOB,
} from "../graphql/aiOperations";

export type AiCalculationJob = NonNullable<
  ResultOf<typeof CALCULATION_JOB>["calculationJob"]
>;

/** The `AiCalculationJobStatus` enum, as inferred from the schema — a union of the
 *  valid status literals, not a bare string, so typos fail to compile. */
export type AiJobStatus = AiCalculationJob["status"];

type ExtractionHeaderInput = VariablesOf<
  typeof CREATE_DRAFT_CALCULATION
>["input"]["header"];

export type AiPhase =
  | { kind: "input" }
  | { kind: "running"; jobId: string; status: AiJobStatus }
  | { kind: "ready"; job: AiCalculationJob }
  | { kind: "noData"; job: AiCalculationJob }
  | { kind: "failed"; job?: AiCalculationJob };

const pollIntervalMs = 2500;
// Client STALE ceiling: past this, a still-non-terminal job flips to failed/retry.
// Kept in lockstep with the backend `trackwise.ai.execution.stale-threshold` (5m) so the
// client never offers Retry before the backend itself would treat the job as stale.
const staleCeilingMs = 5 * 60 * 1000;

const terminalStatuses = new Set<AiJobStatus>([
  "READY",
  "READY_NO_DATA",
  "FAILED",
  "STALE",
]);

interface UseAiDraftResult {
  phase: AiPhase;
  start: (
    header: ExtractionHeaderInput,
    fileRefs: string[],
    message: string | null,
  ) => Promise<void>;
  retry: () => Promise<void>;
  reset: () => void;
}

const derivePhase = (
  currentPhase: AiPhase,
  job: AiCalculationJob,
  startedAt: number | null,
): AiPhase => {
  if (currentPhase.kind !== "running") return currentPhase;

  if (startedAt !== null && Date.now() - startedAt > staleCeilingMs) {
    return { kind: "failed", job };
  }

  const { status } = job;
  if (!terminalStatuses.has(status)) {
    // Non-terminal: surface the live status (PENDING/PARSING/MAPPING) so the
    // running UI reflects progress. Keep the same reference when unchanged.
    return status === currentPhase.status
      ? currentPhase
      : { ...currentPhase, status };
  }

  if (status === "READY") return { kind: "ready", job };
  if (status === "READY_NO_DATA") return { kind: "noData", job };
  return { kind: "failed", job };
};

const useAiDraft = (): UseAiDraftResult => {
  const [phase, setPhase] = useState<AiPhase>({ kind: "input" });
  const jobIdRef = useRef<string | null>(null);
  const startedAtRef = useRef<number | null>(null);

  const [runCreate] = useMutation(CREATE_DRAFT_CALCULATION, {
    errorPolicy: "all",
  });
  const [runRetry] = useMutation(RETRY_DRAFT_CALCULATION, {
    errorPolicy: "all",
  });

  const isRunning = phase.kind === "running";
  const currentJobId = phase.kind === "running" ? phase.jobId : null;
  const {
    data: jobData,
    stopPolling,
    startPolling,
  } = useQuery(CALCULATION_JOB, {
    variables: { jobId: currentJobId ?? "" },
    skip: !isRunning || !currentJobId,
    pollInterval: pollIntervalMs,
    fetchPolicy: "network-only",
  });

  // Derive the next phase from the latest poll data in an effect (not during
  // render). Passing startedAtRef.current lets derivePhase enforce the client
  // STALE ceiling; stopPolling fires once the job reaches a terminal status.
  const job = jobData?.calculationJob ?? null;
  useEffect(() => {
    if (!isRunning || !job) return;
    if (terminalStatuses.has(job.status)) stopPolling();
    setPhase((prev) => derivePhase(prev, job, startedAtRef.current));
  }, [isRunning, job, stopPolling]);

  // Periodic STALE ceiling check — a slow/stuck job that never reaches a
  // terminal status still flips to failed once the ceiling is exceeded.
  useEffect(() => {
    if (!isRunning) return;

    const intervalId = setInterval(() => {
      if (startedAtRef.current === null) return;
      if (Date.now() - startedAtRef.current > staleCeilingMs) {
        stopPolling();
        setPhase((prev) =>
          prev.kind === "running" ? { kind: "failed" } : prev,
        );
      }
    }, pollIntervalMs);

    return () => clearInterval(intervalId);
  }, [isRunning, stopPolling, setPhase]);

  const start = useCallback(
    async (
      header: ExtractionHeaderInput,
      fileRefs: string[],
      message: string | null,
    ) => {
      const { data } = await runCreate({
        variables: { input: { header, fileRefs, message } },
      });

      if (!data?.createDraftCalculation) {
        setPhase({ kind: "failed" });
        return;
      }

      const { jobId, status } = data.createDraftCalculation;
      jobIdRef.current = jobId;
      startedAtRef.current = Date.now();
      setPhase({ kind: "running", jobId, status });
    },
    [runCreate],
  );

  const retry = useCallback(async () => {
    const jobId = jobIdRef.current;
    if (!jobId) return;

    const { data } = await runRetry({ variables: { jobId } });

    const job = data?.retryDraftCalculation;
    if (!job) {
      setPhase({ kind: "failed" });
      return;
    }

    // The backend only re-runs FAILED / stale jobs. A terminal status here means
    // it wasn't eligible (e.g. READY_NO_DATA) — entering "running" would just
    // flash and revert on the next poll, so surface a failed state instead.
    if (terminalStatuses.has(job.status)) {
      setPhase({ kind: "failed" });
      return;
    }

    startedAtRef.current = Date.now();
    setPhase({ kind: "running", jobId, status: job.status });
    startPolling(pollIntervalMs);
  }, [runRetry, startPolling]);

  const reset = useCallback(() => {
    jobIdRef.current = null;
    startedAtRef.current = null;
    setPhase({ kind: "input" });
  }, []);

  return { phase, start, retry, reset };
};

export default useAiDraft;
